import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDevMode } from './DevModeProvider';
import { slugForPathname } from './qaRouteMap';
import { useQaSurfaces, useQaSurfaceMutations } from './useQaSurfaces';

/**
 * Floating in-context Dev Mode dock, rendered on every page while Dev Mode is
 * enabled. When the current route maps to a QA surface (via qaRouteMap), it
 * surfaces Verify / Needs-fixing actions so a platform admin can mark the
 * surface right after testing it. Otherwise it shows a compact link back to
 * the hub.
 */
export function DevModeDock() {
  const { enabled } = useDevMode();
  const location = useLocation();
  const { surfaces } = useQaSurfaces();
  const { setVerdict } = useQaSurfaceMutations();
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');

  if (!enabled) return null;

  const slug = slugForPathname(location.pathname);
  const surface = slug ? surfaces.find((s) => s.slug === slug) ?? null : null;

  const mark = (verdict: 'verified' | 'needs_fixing', noteText?: string) => {
    if (!surface) return;
    setVerdict.mutate({
      slug: surface.slug,
      kind: surface.kind,
      verdict,
      note: noteText ?? null,
    });
    setNoteOpen(false);
    setNote('');
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[19rem] max-w-[calc(100vw-2rem)] rounded-lg border border-rule-strong bg-paper shadow-xl">
      <div className="flex items-center justify-between gap-2 border-b border-rule px-3 py-2">
        <span className="text-[11px] font-medium uppercase tracking-wider text-[#A8451E]">
          Dev Mode
        </span>
        <Link
          to="/dev"
          className="text-xs text-muted underline underline-offset-2 hover:text-ink"
        >
          Hub
        </Link>
      </div>

      <div className="px-3 py-3 text-sm">
        {surface ? (
          <>
            <p className="mb-0.5 text-xs text-muted">
              This page · {surface.kind} · {surface.status}
            </p>
            <p className="mb-2 font-medium leading-snug text-ink">{surface.title}</p>
            {surface.verdict ? (
              <p className="mb-2 text-xs text-muted">
                Current verdict:{' '}
                <strong
                  className={
                    surface.verdict === 'verified' ? 'text-emerald-700' : 'text-[#A8451E]'
                  }
                >
                  {surface.verdict === 'verified' ? 'Verified' : 'Needs fixing'}
                </strong>
                {surface.needsReverify ? ' (re-verify — checklist reset)' : ''}
              </p>
            ) : null}

            {noteOpen ? (
              <div className="flex flex-col gap-2">
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What's not ready? (optional)"
                  rows={3}
                  className="w-full resize-none rounded border border-rule bg-paper-2 px-2 py-1 text-sm text-ink outline-none focus:border-rule-strong"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => mark('needs_fixing', note.trim() || undefined)}
                    disabled={setVerdict.isPending}
                    className="flex-1 rounded bg-[#A8451E] px-2 py-1 text-xs font-medium text-paper hover:opacity-90 disabled:opacity-50"
                  >
                    Save needs-fixing
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNoteOpen(false);
                      setNote('');
                    }}
                    className="rounded border border-rule px-2 py-1 text-xs text-muted hover:text-ink"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => mark('verified')}
                  disabled={setVerdict.isPending}
                  className="flex-1 rounded bg-emerald-700 px-2 py-1 text-xs font-medium text-paper hover:opacity-90 disabled:opacity-50"
                >
                  Verify
                </button>
                <button
                  type="button"
                  onClick={() => setNoteOpen(true)}
                  disabled={setVerdict.isPending}
                  className="flex-1 rounded border border-[#A8451E] px-2 py-1 text-xs font-medium text-[#A8451E] hover:bg-[#A8451E]/5 disabled:opacity-50"
                >
                  Needs fixing
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-xs text-muted">
            This page isn&rsquo;t mapped to a single QA surface. Open the{' '}
            <Link to="/dev" className="underline underline-offset-2 hover:text-ink">
              hub
            </Link>{' '}
            to pick what to verify.
          </p>
        )}
      </div>
    </div>
  );
}
